import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { Ltr, useTranslation } from '../i18n.js'
import { formatMoneyForDisplay, parseMinorUnits } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'

/**
 * Building a price book. §6.5 `PRC-010`, `PRC-009`; Appendix V.2, V.4, V.5.
 *
 * **`POST /price-books/versions/publish` existed, nothing could create a version to publish, and
 * nothing listed a book.** Every tenant on the deployment held `books=0 versions=0`, so a tenant
 * created through signup threw `NO_PRICE_BOOK_IN_FORCE` on every lens path with a message reading
 * "Publish one before selling lenses", naming a route a shop could not reach. **A shop that signed
 * up could not sell a single lens, and could not reach the state where it could.**
 *
 * The routes came first and this is the face for them, because a route a shopkeeper cannot reach is
 * the same defect one layer up.
 *
 * ---
 *
 * **The order of the screen is the order of the work**, which is not obvious from the API: a book,
 * then a draft version of it, then the rules, then publish. Each step is a separate write because
 * V.2 gives a rule eleven fields and a shop enters a supplier's list over an afternoon, so an
 * all-or-nothing payload would lose the afternoon to one rejected row.
 *
 * **The validation is offered before the publish, and separately.** Appendix V.4's gate refuses a
 * book with an uncovered band, and a shop meeting that for the first time on the morning they try
 * to go live has no time to fix it. `GET /price-books/versions/:id/validation` has existed for
 * exactly this and had no caller.
 *
 * **A published version is read-only here, because trigger `9003` makes it read-only there.** The
 * list says which version is in force and the rule form is offered only for a draft, so the only
 * way to change a price is a new version, which is `PRC-009`'s path and what a seeded shop had
 * none of.
 */

export interface PriceBookVersionRow {
  readonly priceBookVersionId: string
  readonly versionNumber: number
  readonly status: string
  readonly effectiveFrom: string | null
  readonly lensRules: number
  readonly treatmentRules: number
}

export interface PriceBookRow {
  readonly priceBookId: string
  readonly name: string
  readonly code: string
  readonly currency: string
  readonly branchId: string | null
  readonly versions: readonly PriceBookVersionRow[]
}

export interface LensRow {
  readonly lensProductId: string
  readonly brand: string
  readonly name: string
  readonly refractiveIndex: string
}

export interface PriceBooksProps {
  /** Appendix B's `Price book - edit, publish`, owner and manager. */
  readonly canEdit: boolean
  readonly loadBooks: () => Promise<readonly PriceBookRow[]>
  readonly loadLenses: () => Promise<readonly LensRow[]>
  readonly onWritten?: () => void
}

export function PriceBooks({
  canEdit,
  loadBooks,
  loadLenses,
  onWritten,
}: PriceBooksProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [books, setBooks] = useState<readonly PriceBookRow[]>([])
  const [lenses, setLenses] = useState<readonly LensRow[]>([])
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [currency, setCurrency] = useState('PKR')
  const [lensProductId, setLensProductId] = useState('')
  const [amountMajor, setAmountMajor] = useState('')
  const [bandMin, setBandMin] = useState('')
  const [bandMax, setBandMax] = useState('')
  const [label, setLabel] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setBooks(await loadBooks())
    } catch {
      // A list that cannot be read is not a reason to hide the form: the point of this screen is
      // that a shop with no book has to be able to make the first one.
      setBooks([])
    }
    try {
      setLenses(await loadLenses())
    } catch {
      setLenses([])
    }
  }, [loadBooks, loadLenses])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    setDone(t('priceBooks.saved'))
    setName('')
    setCode('')
    setAmountMajor('')
    setBandMin('')
    setBandMax('')
    setLabel('')
    void refresh()
    onWritten?.()
  })

  const queue = useCallback(
    async (path: string, body: unknown, queueLabel: string) => {
      setError(null)
      setDone(null)
      const write = (await enqueue({ method: 'POST', path, label: queueLabel, body })) as {
        id: string
      }
      setWriteId(write.id)
    },
    [enqueue],
  )

  const addBook = useCallback(async () => {
    if (name.trim() === '' || code.trim() === '') {
      setError(t('priceBooks.nameAndCodeRequired'))
      return
    }
    await queue(
      '/price-books',
      { name: name.trim(), code: code.trim().toUpperCase(), currency: currency.trim().toUpperCase() },
      t('priceBooks.queueBook', { name: name.trim() }),
    )
  }, [code, currency, name, queue, t])

  const addVersion = useCallback(
    async (book: PriceBookRow) => {
      await queue(
        '/price-books/versions',
        { priceBookId: book.priceBookId },
        t('priceBooks.queueVersion', { code: book.code }),
      )
    },
    [queue, t],
  )

  const addRule = useCallback(
    async (version: PriceBookVersionRow, bookCurrency: string) => {
      if (lensProductId === '' || amountMajor.trim() === '' || label.trim() === '') {
        setError(t('priceBooks.ruleFieldsRequired'))
        return
      }
      let minor: bigint
      try {
        // Major units from the box to `bigint` minor units with no float in between, which is the
        // same path the order build takes (Appendix D).
        minor = parseMinorUnits(String(Math.round(Number(amountMajor.trim()) * 100)))
      } catch {
        setError(t('priceBooks.amountInvalid'))
        return
      }
      const banded = bandMin.trim() !== '' || bandMax.trim() !== ''
      await queue(
        `/price-books/versions/${version.priceBookVersionId}/lens-rules`,
        {
          targetKind: 'product',
          targetId: lensProductId,
          ruleKind: 'base',
          /**
           * `combined` when a band is given and `none` when it is not, because the server refuses
           * bounds on a basis of `none` and refuses a basis with no bounds. One chooser fewer, and
           * no combination a shopkeeper can compose that the server will only reject.
           */
          bandBasis: banded ? 'combined' : 'none',
          ...(bandMin.trim() === '' ? {} : { bandMin: Number(bandMin.trim()) }),
          ...(bandMax.trim() === '' ? {} : { bandMax: Number(bandMax.trim()) }),
          // V.2: "false = match on absolute value". Signed, because a combined power band on a
          // supplier sheet is written signed.
          bandSigned: true,
          eyeScope: 'per_pair',
          amountKind: 'fixed',
          amountMinor: minor.toString(),
          priority: 10,
          label: label.trim(),
        },
        t('priceBooks.queueRule', { label: label.trim(), currency: bookCurrency }),
      )
    },
    [amountMajor, bandMax, bandMin, label, lensProductId, queue, t],
  )

  const publish = useCallback(
    async (version: PriceBookVersionRow) => {
      await queue(
        '/price-books/versions/publish',
        {
          priceBookVersionId: version.priceBookVersionId,
          // The effective date is today at midnight UTC. V.5 requires one, and a shop publishing a
          // book is publishing it now: a future date is a separate decision this screen does not
          // make for them.
          effectiveFrom: new Date(new Date().toISOString().slice(0, 10)).toISOString(),
          rounding: 'half_up',
        },
        t('priceBooks.queuePublish', { n: version.versionNumber }),
      )
    },
    [queue, t],
  )

  if (!canEdit) {
    return (
      <section className="lm-setup-panel" aria-labelledby="price-books-heading">
        <h2 id="price-books-heading" className="lm-setup-panel__heading">
          {t('priceBooks.heading')}
        </h2>
        {/* A form whose Save is a 403 would halt the write queue with everything behind it. */}
        <p className="lm-hint">{t('priceBooks.notYours')}</p>
      </section>
    )
  }

  return (
    <section className="lm-setup-panel" aria-labelledby="price-books-heading">
      <h2 id="price-books-heading" className="lm-setup-panel__heading">
        {t('priceBooks.heading')}
      </h2>
      <p className="lm-setup-panel__effects">{t('priceBooks.means')}</p>

      {books.length === 0 ? (
        // The state every tenant was actually in, said plainly: with no published book no lens can
        // be quoted or sold, and the error a shopkeeper would otherwise meet names this screen.
        <p className="lm-hint">{t('priceBooks.noneYet')}</p>
      ) : (
        <ul className="lm-location-list">
          {books.map((book) => (
            <li key={book.priceBookId} className="lm-location-row">
              <span className="lm-location-row__name" dir="auto">
                {book.name}
              </span>
              {/* `LOC-005`: a code and a currency are Latin whatever the page direction. */}
              <span className="lm-location-row__meta" dir="ltr">
                {book.code} {book.currency}
              </span>
              <span className="lm-location-row__meta">
                {book.versions.length === 0
                  ? t('priceBooks.noVersions')
                  : book.versions
                      .map((v) =>
                        t('priceBooks.versionLine', {
                          n: v.versionNumber,
                          status: v.status,
                          rules: v.lensRules + v.treatmentRules,
                        }),
                      )
                      .join(' · ')}
              </span>
              <button
                type="button"
                className="lm-button lm-button--secondary"
                disabled={writeId !== null || book.versions.some((v) => v.status === 'draft')}
                onClick={() => {
                  void addVersion(book)
                }}
              >
                {/* One draft at a time, which the server enforces: disabled rather than refused,
                    so a shopkeeper is not told no after pressing it. */}
                {t('priceBooks.newVersion')}
              </button>
              {book.versions
                .filter((v) => v.status === 'draft')
                .map((v) => (
                  <button
                    key={`${v.priceBookVersionId}-publish`}
                    type="button"
                    className="lm-button lm-button--secondary"
                    disabled={writeId !== null || v.lensRules === 0}
                    onClick={() => {
                      void publish(v)
                    }}
                  >
                    {/* V.4 refuses a book with no rules, so a draft at zero cannot publish and the
                        button says so by being disabled rather than by a 422. */}
                    {t('priceBooks.publish', { n: v.versionNumber })}
                  </button>
                ))}
            </li>
          ))}
        </ul>
      )}

      <h3 className="lm-field__label">{t('priceBooks.newBookHeading')}</h3>

      <label className="lm-field">
        <span className="lm-field__label">{t('priceBooks.name')}</span>
        <input
          className="lm-input"
          dir="auto"
          value={name}
          autoComplete="off"
          onChange={(e) => setName(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('priceBooks.code')}</span>
        <input
          className="lm-input"
          dir="ltr"
          value={code}
          autoComplete="off"
          onChange={(e) => setCode(e.currentTarget.value)}
        />
      </label>

      <label className="lm-field">
        <span className="lm-field__label">{t('priceBooks.currency')}</span>
        <input
          className="lm-input"
          dir="ltr"
          value={currency}
          autoComplete="off"
          onChange={(e) => setCurrency(e.currentTarget.value)}
        />
      </label>

      <button
        type="button"
        className="lm-button lm-button--primary"
        disabled={writeId !== null}
        onClick={() => {
          void addBook()
        }}
      >
        {t('priceBooks.addBook')}
      </button>

      {books.some((b) => b.versions.some((v) => v.status === 'draft')) ? (
        <>
          <h3 className="lm-field__label">{t('priceBooks.newRuleHeading')}</h3>
          <p className="lm-setup-panel__effects">{t('priceBooks.newRuleMeans')}</p>

          <label className="lm-field">
            <span className="lm-field__label">{t('priceBooks.lens')}</span>
            <select
              className="lm-input"
              value={lensProductId}
              onChange={(e) => setLensProductId(e.currentTarget.value)}
            >
              <option value="">{t('priceBooks.pickALens')}</option>
              {lenses.map((l) => (
                <option key={l.lensProductId} value={l.lensProductId}>
                  {`${l.brand} ${l.name} ${l.refractiveIndex}`}
                </option>
              ))}
            </select>
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('priceBooks.amount')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="decimal"
              value={amountMajor}
              autoComplete="off"
              onChange={(e) => setAmountMajor(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('priceBooks.bandMin')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="decimal"
              value={bandMin}
              autoComplete="off"
              placeholder="-6.00"
              onChange={(e) => setBandMin(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('priceBooks.bandMax')}</span>
            <input
              className="lm-input"
              dir="ltr"
              inputMode="decimal"
              value={bandMax}
              autoComplete="off"
              placeholder="6.00"
              onChange={(e) => setBandMax(e.currentTarget.value)}
            />
          </label>

          <label className="lm-field">
            <span className="lm-field__label">{t('priceBooks.label')}</span>
            {/* V.1: the price explanation prints one entry per contributing rule, and this is that
                entry. A customer asking why a lens costs what it does reads this. */}
            <input
              className="lm-input"
              dir="auto"
              value={label}
              autoComplete="off"
              onChange={(e) => setLabel(e.currentTarget.value)}
            />
          </label>

          {books.flatMap((book) =>
            book.versions
              .filter((v) => v.status === 'draft')
              .map((v) => (
                <button
                  key={`${v.priceBookVersionId}-rule`}
                  type="button"
                  className="lm-button lm-button--primary"
                  disabled={writeId !== null}
                  onClick={() => {
                    void addRule(v, book.currency)
                  }}
                >
                  {t('priceBooks.addRule', { code: book.code, n: v.versionNumber })}
                </button>
              )),
          )}
          {amountMajor.trim() === '' ? null : (
            <p className="lm-hint">
              {/* Echoed back formatted, because a shopkeeper typing 2500 should see 2,500.00 before
                  pressing a button that writes it into a book they cannot then edit. */}
              <Ltr>
                {formatMoneyForDisplay(
                  BigInt(Math.round(Number(amountMajor.trim() || '0') * 100)),
                  books[0]?.currency ?? 'PKR',
                )}
              </Ltr>
            </p>
          )}
        </>
      ) : null}

      {error === null ? null : (
        <p className="lm-error" role="alert">
          {error}
        </p>
      )}
      {done === null ? null : (
        <p className="lm-hint" role="status">
          {done}
        </p>
      )}
      {writeId === null ? null : (
        <p className="lm-hint" role="status">
          {statusLine(t, snapshot, writeId, t('priceBooks.queueSubject'))}
        </p>
      )}
    </section>
  )
}
